import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { useReducer } from 'react';

type State = {
    loading: boolean;
    saving: boolean;
};

type Action =
    | { type: 'startLoading' }
    | { type: 'finishLoading' }
    | { type: 'startSaving' }
    | { type: 'finishSaving' }

const initialState: State = {
    loading: false,
    saving: false,
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'startLoading':
            return { ...state, loading: true };
        case 'finishLoading':
            return { ...state, loading: false };
        case 'startSaving':
            return { ...state, saving: true };
        case 'finishSaving':
            return { ...state, saving: false };
        default:
            return state;
    }
};
export class ResultEither<T> {
    private constructor(
        private readonly _isSuccess: boolean,
        public readonly value?: T,
        public readonly errorMessage?: string
    ) { }

    static ok<T>(value: T): ResultEither<T> {
        return new ResultEither(true, value);
    }

    static fail<T>(errorMessage: string): ResultEither<T> {
        return new ResultEither(false, undefined, errorMessage);
    }

    getOrThrow(): T {
        if (!this._isSuccess) {
            throw new Error(this.errorMessage ?? "Unknown error");
        }
        return this.value as T;
    }

    isSuccess(): boolean {
        return this._isSuccess;
    }

    isFail(): boolean {
        return !this._isSuccess;
    }
}

export const useLoadingStates = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { errorMessages, addErrorMessages, clearErrorMessages } = useAlertMessages();
    const startLoading = () => dispatch({ type: 'startLoading' });
    const finishLoading = () => dispatch({ type: 'finishLoading' });
    const startSaving = () => dispatch({ type: 'startSaving' });
    const finishSaving = () => dispatch({ type: 'finishSaving' });

    const withLoading = async <T>(fn: () => Promise<T>): Promise<ResultEither<T>> => {
        clearErrorMessages();
        try {
            startLoading();
            return ResultEither.ok(await fn());
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            addErrorMessages(message);
            return ResultEither.fail<T>(message);
        } finally {
            finishLoading();
        }
    };

    const withSaving = async <T>(fn: () => Promise<T>): Promise<ResultEither<T>> => {
        try {
            startSaving();
            return ResultEither.ok(await fn());
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            addErrorMessages(message);
            return ResultEither.fail<T>(message);
        } finally {
            finishSaving();
        }
    };

    return {
        ...state,
        errorMessages,
        startLoading,
        finishLoading,
        startSaving,
        finishSaving,
        addErrorMessages,
        clearErrorMessages,
        withLoading,
        withSaving,
    };
};
